import React, {ReactElement, useEffect, useState} from 'react';
import {Body, Cell, Head, HeaderCell, HeaderRow, Row, SortableCell, Table} from '@zendeskgarden/react-tables';
import {Col, Grid, Row as GridRow} from '@zendeskgarden/react-grid';
import {Checkbox, Field, Input, Label, Toggle} from '@zendeskgarden/react-forms';
import {JobType, PropertyType, RevenueModelType, useFetchPmsPropertiesQuery,
    useListManualPropertiesLazyQuery,
    useSetOriginalRevenueRentSubsidyEnabledMutation,
} from "../../../__generated__/generated_types";
import {Dots} from '@zendeskgarden/react-loaders';
import {Button} from '@zendeskgarden/react-buttons';
import {Tooltip} from "@zendeskgarden/react-tooltips";
import {ReactComponent as AlertIcon} from '@zendeskgarden/svg-icons/src/16/alert-warning-fill.svg';
import {ManualPropertyCreateModal} from './ManualPropertyCreateModal';
import {ManualPropertyUpdateModal} from './ManualPropertyUpdateModal';
import {ManualPropertyFileUploadModal} from './ManualPropertyFileUploadModal';
import SelectionBar from './components/SelectionBar';
import {useFetchManualPropertyTemplateCsv} from './logic/useFetchManualPropertyTemplateCsv';

import * as css from "./styles/manualPropertyStyles.module.scss";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {ManualProperty, PMSProperty} from './logic/types';
import {RunningJobRendererLatestJob} from "../../admin/jobs/RunningJobRenderer";
import {Body as ModalBody, Close, Header, Modal} from "@zendeskgarden/react-modals";
import {ListJobs, ListJobsDisplayColumn} from "../../admin/jobs/ListJobs";
import {History} from "@material-ui/icons";
import {getRevenueModelDisplayName} from "../../../constants/Strings";
import {RevenueModelModal} from "../common/RevenueModelModal";
import {toast} from "react-toastify";
import {itemId, useMultiSelect} from "../../../utils/hooks/useMultiSelect";

type ManualPropertyWithPms = ManualProperty & {
    inAlert: boolean;
    originalRevenueModelSubsidyEnabled: boolean;
};

interface ManualPropertyListProps {
    budgetYear: number
}

export function ManualPropertyList(props: ManualPropertyListProps): ReactElement {
    const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(false);
    const [createManualModalVisible, setCreateManualModalVisible] = useState<boolean>(false);
    const [createStandardModalVisible, setCreateStandardModalVisible] = useState<boolean>(false);
    const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
    const [selectedProperty, setSelectedProperty] = useState<ManualProperty | undefined>(undefined);
    const [fileUploadVisible, setFileUploadVisible] = useState<boolean>(false);
    const [search, setSearch] = useState<string | undefined>(undefined);
    const [filteredProperties, setFilteredProperties] = useState<ManualPropertyWithPms[]>([]);
    const {
        selected: selectedProperties,
        isSelected,
        handleSelect,
        selectAll,
        allSelected,
        someSelected,
    } = useMultiSelect(filteredProperties, itemId);
    const [mappedProperties, setMappedProperties] = useState<ManualProperty[]>();
    const [mappedPmsProperties, setMappedPmsProperties] = useState<PMSProperty[]>();
    const [sortColumn, setSortColumn] = useState<{key: keyof ManualPropertyWithPms, direction: "asc" | "desc"} | undefined>({
        key: "name",
        direction: "asc"
    });
    const [currentJobId, setCurrentJobId] = useState<string | undefined>(undefined);
    const [showJobLog, setShowJobLog] = useState<boolean>(false);
    const [latestPMSProperties, setLatestPMSProperties] = useState<Set<string>>();
    const [setRevenueModelVisible, setSetRevenueModelVisible] = useState<boolean>(false);

    const [fetchManualProperties, {data, loading}] = useListManualPropertiesLazyQuery({
        fetchPolicy: 'no-cache',
    });
    const [setOriginalRevenueModelSubsidyEnabled] = useSetOriginalRevenueRentSubsidyEnabledMutation({
        notifyOnNetworkStatusChange: true
    });
    const {data: pmsProperties, loading: pmsPropertiesLoading} = useFetchPmsPropertiesQuery({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "no-cache"
    });

    const {refreshProperties} = useProperties();
    const {fetchTemplate} = useFetchManualPropertyTemplateCsv(props.budgetYear);

    const loadData = () => {
        fetchManualProperties({
            variables: {
                includeDeactivated: true,
                budgetYear: props.budgetYear
            }
        });
    };

    useEffect(() => {
        loadData();

        const handleEsc = (evt: KeyboardEvent) => {
            if (evt.key === 'Escape') {
                setSelectedProperty(undefined);
            }
        };

        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, []);

    useEffect(() => {
        if (!pmsProperties || pmsPropertiesLoading) {
            return;
        }

        let latest: Set<string> | undefined = undefined;
        let mappedPmsProperties: PMSProperty[] | undefined = undefined;
        if (pmsProperties.fetchPMSProperties.length > 0) {
            latest = new Set<string>();
            const maxSeenLatest = pmsProperties.fetchPMSProperties.maxValue(property => property.seenLatest);
            mappedPmsProperties = [];
            for (const pmsProperty of pmsProperties.fetchPMSProperties) {
                if (pmsProperty.seenLatest === maxSeenLatest) {
                    mappedPmsProperties.push({...pmsProperty});
                    latest.add(pmsProperty.name);
                }
            }
        }

        setMappedPmsProperties(mappedPmsProperties);
        setLatestPMSProperties(latest);
    }, [pmsProperties, pmsPropertiesLoading]);

    useEffect(() => {
        if(!data || loading) {
            return;
        }

        setMappedProperties([...data.listManualProperties]);
        // update selected property including deselect property if selected property is not in the loaded properties
        let reloadedSelectedProperty: ManualProperty | undefined = undefined;
        if(selectedProperty) {
            reloadedSelectedProperty = data.listManualProperties.find(p => p.id === selectedProperty.id);
        }
        setSelectedProperty(reloadedSelectedProperty);
        let filtered = data.listManualProperties;
        if(!includeDeactivated) {
            filtered = filtered.filter(d => !d.isDeactivated);
        }
        if(search) {
            filtered = filtered.filter(d => d.name.toLowerCase().includes(search.toLowerCase() ) || d.systemName.toLowerCase().includes(search.toLowerCase()));
        }

        // Enrich with inAlert first
        const enriched = filtered.map(property => ({
            ...property,
            inAlert: property.propertyType == PropertyType.Standard && Boolean(latestPMSProperties && !latestPMSProperties.has(property.systemName))
        }));

        // Always show properties with inAlert==true first, then apply optional sort
        let sorted = enriched;
        if(sortColumn) {
            // Sort by inAlert first (true comes first), then by the selected column
            sorted = enriched.sort((a, b) => {
                // First, sort by inAlert (true first)
                if (a.inAlert !== b.inAlert) {
                    return a.inAlert ? -1 : 1;
                }
                // Then apply the selected sort
                const aValue = a[sortColumn.key];
                const bValue = b[sortColumn.key];
                if (aValue < bValue) {
                    return sortColumn.direction === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortColumn.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        } else {
            // Even without sortColumn, still sort by inAlert first
            sorted = enriched.sort((a, b) => {
                if (a.inAlert !== b.inAlert) {
                    return a.inAlert ? -1 : 1;
                }
                return 0;
            });
        }

        setFilteredProperties(sorted);
    }, [data, loading, search, sortColumn, latestPMSProperties, includeDeactivated]);

    const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIncludeDeactivated(event.target.checked);
    };

    function setSort(column: keyof ManualPropertyWithPms, currentDirection: "asc" | "desc" | undefined): void {
        if(currentDirection === "desc") {
            setSortColumn(undefined);
            return;
        }

        const newDirection = currentDirection === "asc" ? "desc" : "asc";
        setSortColumn({
            key: column,
            direction: newDirection
        });
    }

    useEffect(() => {
        if(selectedProperties.length === 1) {
            setSelectedProperty(selectedProperties[0]);
        } else {
            setSelectedProperty(undefined);
        }
    }, [selectedProperties]);

    const openCreateManualModal = () => {
        setCreateManualModalVisible(true);
    };

    const closeCreateManualModal = () => {
        setCreateManualModalVisible(false);
    };

    const openCreateStandardModal = () => {
        setCreateStandardModalVisible(true);
    };

    const closeCreateStandardModal = () => {
        setCreateStandardModalVisible(false);
    };

    const openEditModal = () => {
        if(selectedProperty) {
            setEditModalVisible(true);
        }
    };

    const closeEditModal = () => {
        setEditModalVisible(false);
    };

    const refreshData = () => {
        loadData();
        refreshProperties();
    };

    const getMonthName = (monthIndex: number): string => {
        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        return months[monthIndex] || `Month ${monthIndex}`;
    };

    return (
        <>
            <Grid>
                <GridRow className={css.jobRow}>
                    <Col textAlign="end">
                        <span className={css.jobStatus}>
                            <RunningJobRendererLatestJob jobType={JobType.ManualPropertyParseFiles} jobId={currentJobId} />
                        </span>
                        <Button size="small" onClick={() => setShowJobLog(true)}>
                            <Button.StartIcon><History /></Button.StartIcon>
                            View Job History
                        </Button>
                    </Col>
                </GridRow>
                <GridRow>
                    <Col>
                        <div className={css.headerActions}>
                            <Field>
                                <Input
                                        type="text"
                                        placeholder="Property Name"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        aria-label="Search property list"
                                />
                            </Field>
                            <Field>
                                <Toggle onChange={handleToggleChange} checked={includeDeactivated}>
                                    <Label>Include Deactivated</Label>
                                </Toggle>
                            </Field>
                            <div>
                                <Button isPrimary onClick={openCreateManualModal}>Create Property</Button>
                                <Button isPrimary onClick={openCreateStandardModal}>Onboard Property</Button>
                            </div>
                        </div>
                    </Col>
                </GridRow>
                <GridRow>
                    <Col>
                        {loading ? (
                                <Dots size={32}/>
                        ) : (
                                <Table className={css.table}>
                                    <Head>
                                        <HeaderRow>
                                            <HeaderCell isMinimum>
                                                <Field className={css.checkboxCell}>
                                                    <Checkbox
                                                            checked={allSelected}
                                                            indeterminate={someSelected}
                                                            disabled={filteredProperties.length === 0}
                                                            onChange={e => selectAll(e.target.checked)}
                                                            aria-label="Select all properties"
                                                    >
                                                        <Label />
                                                    </Checkbox>
                                                </Field>
                                            </HeaderCell>
                                            <SortableCell
                                                onClick={() => setSort("name", sortColumn?.key === "name" ? sortColumn?.direction : undefined)}
                                                sort={sortColumn && sortColumn.key === "name" ? sortColumn.direction : undefined}
                                            >
                                                Name
                                            </SortableCell>
                                            <HeaderCell>Reforecast Year</HeaderCell>
                                            <SortableCell
                                                onClick={() => setSort("reforecastStartMonthIndex", sortColumn?.key === "reforecastStartMonthIndex" ? sortColumn?.direction : undefined)}
                                                sort={sortColumn && sortColumn.key === "reforecastStartMonthIndex" ? sortColumn.direction : undefined}
                                            >
                                                Reforecast Start Month
                                            </SortableCell>
                                            <HeaderCell>
                                                Revenue Model
                                            </HeaderCell>
                                            <SortableCell
                                                onClick={() => setSort("propertyType", sortColumn?.key === "propertyType" ? sortColumn?.direction : undefined)}
                                                sort={sortColumn && sortColumn.key === "propertyType" ? sortColumn.direction : undefined}
                                            >
                                                Is Manual?
                                            </SortableCell>
                                            <SortableCell
                                                onClick={() => setSort("originalRevenueModelSubsidyEnabled", sortColumn?.key === "originalRevenueModelSubsidyEnabled" ? sortColumn?.direction : undefined)}
                                                sort={sortColumn && sortColumn.key === "originalRevenueModelSubsidyEnabled" ? sortColumn.direction : undefined}
                                            >
                                                Rent Subsidy Enabled
                                            </SortableCell>
                                            <SortableCell
                                                onClick={() => setSort("isDeactivated", sortColumn?.key === "isDeactivated" ? sortColumn?.direction : undefined)}
                                                sort={sortColumn && sortColumn.key === "isDeactivated" ? sortColumn.direction : undefined}
                                            >
                                                Status
                                            </SortableCell>
                                        </HeaderRow>
                                    </Head>
                                    <Body>
                                        {filteredProperties.length === 0 ? (
                                                <Row>
                                                    <Cell colSpan={7}>No properties found</Cell>
                                                </Row>
                                        ) : (
                                                filteredProperties.map((property, index) => (
                                                        <Row
                                                            key={property.id}
                                                            style={{verticalAlign: "middle"}}
                                                        >
                                                            <Cell isMinimum>
                                                                <Field className={css.checkboxCell}>
                                                                    <Checkbox
                                                                            checked={isSelected(property)}
                                                                            onClick={event => handleSelect(event.currentTarget.checked, property, index, event.shiftKey)}
                                                                            onChange={() => {/* shut up react warning */}}
                                                                    >
                                                                        <Label />
                                                                    </Checkbox>
                                                                </Field>
                                                            </Cell>
                                                            <Cell>
                                                                <div className={css.propertyNameCell}>
                                                                    {property.inAlert &&
                                                                        <Tooltip content="Property not found in PMS">
                                                                            <AlertIcon style={{color: "var(--yellow-200)"}} />
                                                                        </Tooltip>
                                                                    }
                                                                    <span>{property.name}</span>
                                                                </div>
                                                                <span style={{opacity: 0.5}}>
                                                                    {property.propertyType === PropertyType.Standard && property.systemName != property.name ? `(${property.systemName})` : ""}
                                                                </span>
                                                            </Cell>
                                                            <Cell>{property.reforecastYear}</Cell>
                                                            <Cell>{getMonthName(property.reforecastStartMonthIndex)}</Cell>
                                                            <Cell>{getRevenueModelDisplayName(property.revenueModel)}</Cell>
                                                            <Cell>{property.propertyType === PropertyType.Manual ? 'Yes' : 'No'}</Cell>
                                                            <Cell>{property.revenueModel === RevenueModelType.Original || property.revenueModel === RevenueModelType.LeaseUp ? (property.originalRevenueModelSubsidyEnabled ? "Yes" : "No") : "N/A"}</Cell>
                                                            <Cell>{property.isDeactivated ? 'Deactivated' : 'Active'}</Cell>
                                                        </Row>
                                                ))
                                        )}
                                    </Body>
                                </Table>
                        )}
                    </Col>
                </GridRow>
            </Grid>

            <ManualPropertyCreateModal
                pmsPropertiesData={undefined}
                existingPropertiesData={undefined}
                isStandard={false}
                formVisible={createManualModalVisible}
                onModalClose={closeCreateManualModal}
                onCreate={refreshData}
                budgetYear={props.budgetYear}
            />

            <ManualPropertyCreateModal
                existingPropertiesData={mappedProperties}
                pmsPropertiesData={mappedPmsProperties}
                isStandard={true}
                formVisible={createStandardModalVisible}
                onModalClose={closeCreateStandardModal}
                onCreate={refreshData}
                budgetYear={props.budgetYear}
            />

            {selectedProperty &&
                <ManualPropertyUpdateModal
                    manualProperty={selectedProperty}
                    existingPropertiesData={mappedProperties}
                    pmsPropertiesData={mappedPmsProperties}
                    formVisible={editModalVisible}
                    onModalClose={closeEditModal}
                    onUpdate={refreshData}
                    budgetYear={props.budgetYear}
                    />
            }

            <SelectionBar
                selected={selectedProperties}
                onEdit={openEditModal}
                isManual={selectedProperty?.propertyType === PropertyType.Manual}
                onFetchTemplates={() => {
                    if(selectedProperty) {
                        fetchTemplate(selectedProperty.id);
                    }
                }}
                onUploadFiles={() => {
                    if(selectedProperty) {
                        setFileUploadVisible(true);
                    }
                }}
                onSetRevenueModel={() => setSetRevenueModelVisible(true)}
                onSetRentSubsidyEnabled={(val: boolean) => {
                    if(selectedProperties.length > 0) {
                        setOriginalRevenueModelSubsidyEnabled({
                            variables: {
                                propertyIds: selectedProperties.map(p => p.id),
                                rentSubsidyEnabled: val,
                                budgetYear: props.budgetYear
                            }
                        }).then(res => {
                            if(res.data?.setOriginalRevenueRentSubsidyEnabled) {
                                toast.success(`Rent Subsidy ${val ? "Enabled" : "Disabled"} for ${selectedProperties.length} Properties.`);
                                refreshData();
                            } else {
                                toast.error(`Error ${val ? "Enabling" : "Disabling"} Rent Subsidy.`);
                            }
                        }).catch(() => {
                            toast.error(`Error ${val ? "Enabling" : "Disabling"} Rent Subsidy.`);
                        });
                    } else {
                        toast.error("No properties selected.");
                    }
                }}
                name="Property"
                namePlural="Properties"
                enableBulk={false}
            />

            {fileUploadVisible && selectedProperty && (
                <ManualPropertyFileUploadModal
                    propertyId={selectedProperty.id}
                    formVisible={fileUploadVisible}
                    jobCreated={(jobId) => setCurrentJobId(jobId)}
                    onModalClose={() => setFileUploadVisible(false)}
                />
            )}

            {showJobLog &&
                    <Modal
                            isLarge
                            onClose={() => setShowJobLog(false)}
                    >
                        <Header>
                            Manual Property Upload Jobs
                        </Header>
                        <ModalBody>
                            <ListJobs
                                    jobType={JobType.ManualPropertyParseFiles}
                                    visibleColumns={[
                                        ListJobsDisplayColumn.user,
                                        ListJobsDisplayColumn.status,
                                        ListJobsDisplayColumn.created,
                                        ListJobsDisplayColumn.started,
                                        ListJobsDisplayColumn.ended,
                                        ListJobsDisplayColumn.errorMessage,
                                        ListJobsDisplayColumn.details,
                                    ]}
                            />
                        </ModalBody>
                        <Close aria-label="Close modal" />
                    </Modal>
            }
            {setRevenueModelVisible && selectedProperties[0] &&
                <RevenueModelModal
                    onUpdate={() => {
                        setSetRevenueModelVisible(false);
                        refreshData();
                    }}
                    onClose={() => setSetRevenueModelVisible(false)}
                    budgetYear={selectedProperties[0].reforecastYear + 1}
                    properties={selectedProperties}
                />
            }
        </>
    );
}
